/* Dual-handle slider facade over a native list facet's checkboxes.
 *
 * Shopify's Online Store filtering has no range support for number
 * metafields (only price), so the mature-height facet arrives as one
 * checkbox per distinct value. This element hides that list and drives
 * it from a slider instead: the handles step over the sorted distinct
 * values (index-based, so a catalogue of mostly-small plants isn't
 * squeezed into the left edge of a linear metre scale), and on release
 * every checkbox inside the chosen range is ticked and the form's
 * normal debounced update runs. Full range = no boxes ticked = filter
 * off. Without JS the checkbox list renders untouched.
 */
if (!customElements.get('altra-range-facet')) {
  class AltraRangeFacet extends HTMLElement {
    connectedCallback() {
      // rendered directly before the value list in both desktop and drawer markup
      this.list =
        this.nextElementSibling?.tagName === 'UL'
          ? this.nextElementSibling
          : this.closest('fieldset')?.querySelector('ul');
      if (!this.list) return;
      this.boxes = Array.from(this.list.querySelectorAll('input[type="checkbox"]'))
        .map((input) => ({ input, v: parseFloat(input.value) }))
        .filter((b) => Number.isFinite(b.v))
        .sort((a, b) => a.v - b.v);
      if (this.boxes.length < 2) return;

      this.list.classList.add('altra-range-facet__hidden-list');
      const last = this.boxes.length - 1;
      const activeIdx = this.boxes.reduce(
        (acc, b, i) => (b.input.checked ? [Math.min(acc[0], i), Math.max(acc[1], i)] : acc),
        [Infinity, -Infinity]
      );
      const lo = activeIdx[0] === Infinity ? 0 : activeIdx[0];
      const hi = activeIdx[1] === -Infinity ? last : activeIdx[1];

      this.innerHTML = `
        <div class="altra-range-facet__readout" aria-hidden="true"></div>
        <div class="altra-range-facet__track-wrap">
          <span class="altra-range-facet__fill"></span>
          <input type="range" min="0" max="${last}" step="1" value="${lo}"
            aria-label="${this.dataset.labelMin || 'Minimum height'}">
          <input type="range" min="0" max="${last}" step="1" value="${hi}"
            aria-label="${this.dataset.labelMax || 'Maximum height'}">
        </div>`;
      [this.minInput, this.maxInput] = this.querySelectorAll('input[type="range"]');
      this.readout = this.querySelector('.altra-range-facet__readout');
      this.fill = this.querySelector('.altra-range-facet__fill');

      for (const input of [this.minInput, this.maxInput]) {
        input.addEventListener('input', () => this.render());
        input.addEventListener('change', () => this.apply());
      }
      this.render();
    }

    bounds() {
      let lo = Math.min(+this.minInput.value, +this.maxInput.value);
      let hi = Math.max(+this.minInput.value, +this.maxInput.value);
      return [lo, hi];
    }

    fmt(v) {
      return v < 1 ? `${Math.round(v * 100)}cm` : `${+v.toFixed(1)}m`;
    }

    render() {
      const [lo, hi] = this.bounds();
      const last = this.boxes.length - 1;
      const full = lo === 0 && hi === last;
      this.readout.textContent = full
        ? this.dataset.labelAny || 'Any height'
        : `${this.fmt(this.boxes[lo].v)} – ${this.fmt(this.boxes[hi].v)}`;
      this.fill.style.left = `${(lo / last) * 100}%`;
      this.fill.style.width = `${((hi - lo) / last) * 100}%`;
      for (const input of [this.minInput, this.maxInput]) {
        const i = +input.value;
        input.setAttribute('aria-valuetext', `${this.fmt(this.boxes[i].v)}`);
      }
    }

    apply() {
      const [lo, hi] = this.bounds();
      const full = lo === 0 && hi === this.boxes.length - 1;
      let changed = false;
      this.boxes.forEach((b, i) => {
        const want = !full && i >= lo && i <= hi && !b.input.disabled;
        if (b.input.checked !== want) {
          b.input.checked = want;
          changed = true;
        }
      });
      if (changed) {
        this.boxes[0].input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }
  customElements.define('altra-range-facet', AltraRangeFacet);
}
